import React, { useState, useEffect, useCallback } from "react";
import { Grid, Box } from "@mui/material";
import Chip from "@mui/material/Chip";
import {
  GridFilterModel,
  GridValueFormatterParams,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";

import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import httpClient from "../../lib/httpClient";
import { apiError } from "../../lib/apiError";
import { initialFilterModel, isThereAnyIncompleteFilter } from "../util/tableUtilsTS";

import {
  capitalizeFirstLetter,
  dateFilterOperators,
  filterModelToQueryValue,
  formatDateTimeValue,
  formatDateValue,
  stringFilterOperators,
  tableCellRender,
} from "../util/tableUtils";
import { EventScopeMap } from "./EventScopeMap";
import { ReqParametersLayer } from "./ReqParametersLayer";

const getStatusColor = (status: EventScopeStatus) => {
  if (status === "submitted") {
    return "orange";
  } else if (status === "in progress") {
    return "blue";
  } else if (status === "available") {
    return "green";
  } else if (status === "failed") {
    return "red";
  }
  return "yellow";
};

export const EventScope = () => {
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [requestsList, setRequestsList] = useState<EventScopeRequest[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [reqBtnDisabledFromMap, setReqBtnDisabledFromMap] = useState<boolean>(false);
  const [refreshTableFlag, setRefreshTableFlag] = useState<boolean>(false);

  const [polygonCountry, setPolygonCountry] = useState<string | null>(null);

  const [newEventReqPolygon, setNewEventReqPolygon] = useState<Partial<EventScopeRequest>>({
    aoiPolygonWKT: "",
  });

  const getRequestsData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));
    let sortField, sortDirection;

    if (sortModel && Object.keys(sortModel).length) {
      const { field, sort } = sortModel[0];
      sortField = field;
      sortDirection = sort;
    }
    const filterQuery = filterModelToQueryValue(filterModel);

    try {
      const { data: eventScopesResponse } = await httpClient().get(
        `/eventScope?page=${paginationModel.page}&pageSize=${
          paginationModel.pageSize
        }&filters=${filterQuery}&orderBy=${sortField}&orderDirection=${sortDirection}`,
      );

      setRequestsList(eventScopesResponse.data);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: eventScopesResponse.total,
      }));
    } catch (error) {
      apiError(error);
      setRequestsList([]);

      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: 0,
      }));
    }
  }, [filterModel, sortModel, paginationModel.page, paginationModel.pageSize]);

  const onFilterChange = useCallback((aFilterModel: GridFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel: GridSortModel) => {
    setSortModel(aSortModel);
  }, []);

  useEffect(() => {
    if (!isThereAnyIncompleteFilter(filterModel)) {
      getRequestsData();
    }
  }, [filterModel, getRequestsData]);

  useEffect(() => {
    if (refreshTableFlag) {
      getRequestsData();
      setRefreshTableFlag(false);
    }
  }, [refreshTableFlag, getRequestsData]);

  const requestsColumns = [
    {
      field: "eventCode",
      headerName: "Event Code",
      flex: 2,
    },
    {
      field: "eventType",
      headerName: "Event Type",
      flex: 1,
    },
    {
      field: "eventLocation",
      headerName: "Event Location",
      flex: 1,
    },
    {
      field: "eventDate",
      headerName: "Event Date",
      type: "date",
      flex: 1,
      filterOperators: dateFilterOperators,
      valueGetter: (params: { row: EventScopeRequest }) =>
        params.row.eventDate && formatDateValue(params.row.eventDate),
    },
    {
      field: "eventRequestDate",
      headerName: "Event Request Date",
      type: "date",
      flex: 2,
      filterOperators: dateFilterOperators,
      valueGetter: (params: { row: EventScopeRequest }) =>
        params.row.eventRequestDate && formatDateTimeValue(params.row.eventRequestDate),
    },

    {
      field: "user",
      headerName: "User",

      flex: 2,
      filterOperators: stringFilterOperators,
      
    },

    {
      field: "eventRequestStatus",
      headerName: "Status",
      flex: 1,
      renderCell: (params: { row: EventScopeRequest }) => {
        return (
          <Chip
            label={capitalizeFirstLetter(params.row.eventRequestStatus)}
            style={{ backgroundColor: getStatusColor(params.row.eventRequestStatus) }}
            size="small"
          />
        );
      },
    },
  ];

  const columns = requestsColumns.map((tableColumn) => ({
    valueFormatter: (params: GridValueFormatterParams) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    ...tableColumn,
  }));

  return (
    <Box mt={3}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <ReqParametersLayer
            polygonCountry={polygonCountry}
            reqBtnDisabledFromMap={reqBtnDisabledFromMap}
            newEventReqPolygon={newEventReqPolygon}
            setNewEventReqPolygon={setNewEventReqPolygon}
            setRefreshTableFlag={setRefreshTableFlag}
          ></ReqParametersLayer>
        </Grid>
        <Grid item xs={12} sm={12}>
          <EventScopeMap
            newEventReqPolygon={newEventReqPolygon}
            setPolygonCountry={setPolygonCountry}
            setReqBtnDisabledFromMap={setReqBtnDisabledFromMap}
            setNewEventReqPolygon={setNewEventReqPolygon}
          ></EventScopeMap>
        </Grid>

        <Grid item xs={12} sm={12}>
          <DataGridCystellarTable
            columns={columns}
            rows={requestsList}
            rowCount={tableState.total}
            getRowId={(rowData: TableRowData<EventScopeRequest>) => rowData._id}
            loading={tableState.isLoading}
            paginationMode={"server"}
            paginationModel={paginationModel}
            pageSizeOptions={[10, 20, 50]}
            onPaginationModelChange={(newPaginationModel: GridPaginationModel) =>
              setPaginationModel(newPaginationModel)
            }
            filterMode={"server"}
            onFilterModelChange={onFilterChange}
            sortingMode={"server"}
            onSortModelChange={onSortChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
